<template>
    <Modal
        :modelValue="currentDialog != null"
        static
        :card="false"
        type="dialog"
        content-class="card"
        :title="currentDialog?.title ?? 'Important Message'"
    >
        <div class="card-header border-bottom-0">
            <div class="dialog-icons p-2" :class="computedTheme">
                <Icon
                    v-if="currentDialog?.variant != null"
                    :icon="currentDialog?.variant as Icons"
                    class="text-white"
                />
            </div>
            <h1 class="text-lg">{{ currentDialog?.title ?? 'Important Message' }}</h1>
        </div>

        <div class="card-body">
            <ErrorHandler v-if="currentDialog?.error != null" :error="currentDialog?.error" :padding="false" />
            <p v-else style="" v-html="currentDialog?.description" />
        </div>

        <div class="card-footer d-flex justify-content-end">
            <!-- Default Buttons -->
            <div v-if="currentDialog?.options == null" class="d-flex flex-row-reverse justify-content-end gap-3">
                <Btn
                    v-if="currentDialog?.agree != null"
                    :class="currentDialog?.agree.class"
                    :variant="currentDialog?.agree.variant ?? currentDialog.variant ?? 'primary'"
                    @click="onOption(currentDialog?.agree)"
                    :disabled="countdowns.agree != null && countdowns.agree > 0"
                >
                    <span>{{ currentDialog?.agree.text ?? 'Ok' }}</span>
                    <span v-if="countdowns.agree != null">({{ countdowns.agree }})</span>
                </Btn>
                <Btn
                    v-if="currentDialog?.disagree != null"
                    :class="currentDialog?.disagree.class ?? 'btn-outline'"
                    :variant="currentDialog?.disagree.variant ?? 'secondary'"
                    @click="onOption(currentDialog?.disagree)"
                    :disabled="countdowns.disagree != null && countdowns.disagree > 0"
                >
                    <span>{{ currentDialog?.disagree.text ?? 'Cancel' }}</span>
                    <span v-if="countdowns.disagree != null"> ({{ countdowns.disagree }})</span>
                </Btn>
            </div>
            <!-- Custom Buttons -->
            <div v-else class="d-flex flex-row-reverse justify-content-end gap-3">
                <template v-for="option in currentDialog.options" :key="option.text">
                    <Btn :variant="option.variant" :class="option.class" @click="onOption(option)">
                        <span>{{ option.text }}</span>
                        <span v-if="option.text != null && countdowns[option.text] != null">
                            ({{ countdowns[option.text] }})</span
                        ></Btn
                    >
                </template>
            </div>
        </div>
    </Modal>
</template>

<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core';
import { useDialogStore, type DialogChoice, type DialogOptions } from '@aka/vue-kit';
import type { Icons } from '../misc/Icon.vue';

const dialogStore = useDialogStore();
const currentDialog = computed(() => dialogStore.currentDialog?.dialog);

const computedTheme = computed(() => {
    return {
        [`bg-${currentDialog.value?.variant}`]: currentDialog.value?.variant != null,
        'text-white': true,
    };
});

const onOption = (option?: DialogChoice) => {
    dialogStore.nextDialog();
    if (option?.onClick != null) {
        option.onClick();
    }
};

const onClose = () => {
    const dialogOptions = currentDialog.value;
    if (dialogOptions == null) {
        dialogStore.nextDialog();
        return;
    }
    if (dialogOptions.disagree?.onClick != null) {
        dialogOptions.disagree.onClick();
    }
    if (dialogOptions.agree?.onClick != null) {
        dialogOptions.agree.onClick();
    }
};

//timer
const dialogChanged = ref(new Date());
watch(currentDialog, () => {
    dialogChanged.value = new Date();
    updateCountdownState();
});
const countdowns = ref<Record<string, number>>({});
const updateCountdownState = () => {
    if (currentDialog.value == null) {
        return;
    }
    const value: Record<string, number> = {};
    const apply = (key: string, timeout?: number) => {
        const remaining =
            timeout == null ? undefined : timeout - (new Date().getTime() - dialogChanged.value.getTime());
        if (remaining != null && remaining > 0) {
            value[key] = Math.ceil(remaining / 1000);
        }
    };
    apply('agree', currentDialog.value.agree?.timeout);
    apply('disagree', currentDialog.value.disagree?.timeout);
    for (const choice of currentDialog.value.options ?? []) {
        if (choice.text != null) {
            apply(choice.text ?? '', choice.timeout);
        }
    }
    countdowns.value = value;
};
useIntervalFn(updateCountdownState, 1000);
</script>

<style>
.light-shadow {
    box-shadow: var(--kt-box-shadow);
}
</style>

<style scoped>
.card-body {
    background-color: rgba(31, 45, 65, 0.03);
}
</style>
